<template>
	<div class="row other-features-container">
		<div
			class="col-md-2 col-sm-4 col-xs-6 feature-icons"
			v-for="feature in features"
			:key="`${ feature.name }-icon`"
		>
			<div :class="['secondary-feature', feature.classes.primary ? feature.classes.primary : '']">
				<div :class="['secondary-feature-icon', feature.classes.secondary ? feature.classes.secondary : '']">
					<img class="m-auto" :src="feature.isActive ? feature.image.active : feature.image.inactive" :alt="feature.name">
				</div>
				<div :class="['secondary-feature-text', { 'inactive-feature': !feature.isActive }]">{{ feature.name }}</div>
			</div>
			<div class="tooltip-container">
				<tooltip :class-binding="feature.name == 'Stream Ready' ? 'specific-tooltip' : ''">
					<template v-if="feature.isActive">
						{{ feature.tooltip.included }}
					</template>
					<template v-else>
						{{ feature.tooltip.missing }}
					</template>
				</tooltip>
			</div>
		</div>
	</div>
</template>

<script>
import Tooltip from "@c/generals/tooltip";

export default {
	name: "IconFeature",
	components: {
		Tooltip
	},
	computed: {
		isRgbActive() {
			const activeRgbRoute = ["streaming-pc", "creator-pc", "h1-mini-pc"];
			return activeRgbRoute.includes(this.$route.name);
		},
		isUsbCActive() {
			const activeUsbCRoutes = ["creator-pc", "h1-mini-pc"];
			return activeUsbCRoutes.includes(this.$route.name);
		},
		features() {
			return [
				{
					name: "RGB Lighting",
					classes: {
						primary: "rgb-lighting"
					},
					image: {
						active: "/images/icons/key-features-icons/lighting-active.svg",
						inactive: "/images/icons/key-features-icons/lighting-inactive.svg"
					},
					tooltip: {
						included: "This build is equipped with RGB components.",
						missing: "This build is not equipped with RGB components."
					},
					isActive: this.isRgbActive
				},
				{
					name: "WiFi",
					classes: {
						primary: "wifi"
					},
					image: {
						active: "/images/icons/key-features-icons/signal-wifi.svg"
					},
					tooltip: {
						included: "This build is WIFI ready.",
						missing: "This build does not have built-in WiFi capability."
					},
					isActive: true
				},
				{
					name: "Bluetooth",
					classes: {
						primary: "bluetooth-box",
						secondary: "bluetooth"
					},
					image: {
						active: "/images/icons/key-features-icons/bluetooth-active.svg"
					},
					tooltip: {
						included: "This build has Bluetooth capability for use with wireless Bluetooth products.",
						missing: "This build does not have Bluetooth capability for use with wireless Bluetooth products."
					},
					isActive: true
				},
				{
					name: "USB-C",
					classes: {
						primary: "usb-c-box",
						secondary: "usb-c"
					},
					image: {
						active: "/images/icons/key-features-icons/usb-type-c-active.svg",
						inactive: "/images/icons/key-features-icons/usb-type-c-inactive.svg"
					},
					tooltip: {
						included: "USB-C port on the front I/O panel of the case is functional.",
						missing: "USB-C port on the front I/O panel of the case is not functional."
					},
					isActive: this.isUsbCActive
				},
				{
					name: "VR Ready",
					classes: {
						primary: "vr-ready"
					},
					image: {
						active: "/images/icons/key-features-icons/dude.svg"
					},
					tooltip: {
						included: "This build meets the minimum recommended specs for VR functionality.",
						missing: "VR is not recommended for this configuration."
					},
					isActive: true
				},
				{
					name: "Stream Ready",
					classes: {
						primary: "stream"
					},
					image: {
						active: "/images/icons/key-features-icons/streaming.svg"
					},
					tooltip: {
						included: "This build will be capable of streaming most games in 720p with 60fps (internet speeds permitting). Games can only be streamed at 60+ FPS when running at 60+ FPS.",
						missing: "Streaming not recommended for this build."
					},
					isActive: true
				}
			];
		}
	}
};
</script>

<style lang="scss" scoped>
@import "~@s/_asset-mixers.scss";

.other-features-container {
	margin-bottom: 43px;

	@media (max-width: 768px) {
		padding: 0 16px;
		margin-bottom: 20px;
	}

	.col-md-3 {
		padding-right: 0;
		padding-left: 0;
	}

	.secondary-feature {
		display: flex;
		justify-content: center;
		align-items: center;
		flex-direction: column;
		height: 110px;
		border-right: 1px solid $light-grey;

		@media (max-width: 767px) {
			border-bottom: 1px solid $light-grey;
		}

		&-icon {
			width: 32px;
			height: 32px;
			margin-bottom: 7px;

			img {
				width: 100%;
			}
		}

		&-text {
			color: $purple;
			font-weight: bold;
		}

		.inactive-feature {
			color: $grey;
		}

		.bluetooth {
			justify-content: center;
			display: flex;
			margin-bottom: 8px;

			img {
				width: 18px;
				height: 36px;
			}
		}

		.usb-c {
			justify-content: center;
			display: flex;
			margin-bottom: 8px;

			img {
				width: 100px;
				height: 36px;
			}
		}
	}

	.rgb-lighting {
		border-left: none;
		@media (max-width: 991px) {
			border-bottom: 1px solid #e3e3e3;
		}
	}

	.vr-ready {
		@media (max-width: 768px) {
			border-right: 1px solid #e3e3e3;
			border-bottom: none;
		}

		div {
			display: flex;

			img {
				width: 24px;
				height: 32px;
			}
		}
	}

	.wifi {
		@media (max-width: 767px) {
			border-right: none;
		}

		@media (max-width: 991px) {
			border-bottom: 1px solid #e3e3e3;
		}

		div {
			margin-bottom: 0;
		}

		.secondary-feature-icon {
			margin: 3px 0;
		}
	}

	.usb-c-box {
		@media (max-width: 767px) {
			border-right: none;
		}
	}

	.stream {
		border-right: none;
		border-bottom: none;

		div {
			img {
				width: 26px;
				height: 30.55px;
			}
		}
	}
}
@media (max-width: 991px) {
	.bluetooth-box {
		border-bottom: 1px solid #e3e3e3;
	}

	.col-sm-4 {
		padding-right: 0;
		padding-left: 0;
	}
}

.col-md-2 {
	padding-right: 0;
	padding-left: 0;
}

.col-sm-3 {
	padding-left: 0;
}

.tooltip-container {
	position: absolute;
	display: none;
}

.feature-icons:hover {
	.tooltip-container {
		display: block;
	}
}

.feature-icons::v-deep {
	.help-tooltip {
		padding: 15px;
		transform: translate(-20%, -230%) !important;
	}

	.specific-tooltip  {
		padding: 15px;
		transform: translate(-20%, -170%) !important;
	}
}
</style>